// View models for the Exceptions tab, and the mapping from flux's raw
// yard-observation decision rows to them. Which rows are exceptions, and of
// which of the four types, is flux's call (its ExceptionPolicy); this only
// translates the type it sends into the names the page uses.

import {
  CreateUnknownTrailerResult,
  GhostTrailerMatch,
  YardObservationDecision
} from 'api/yardReconciliation';
import { ageMinutes, formatAge } from './freshness';
import { cameraHeading } from './geo';
import { rtlsFullscreenImage, rtlsPreviewImage } from './utils';

export type ExceptionType = 'staleYmsEmptySpot' | 'fuzzyMismatch' | 'ghostTrailer';
export type ExceptionSeverity = 'open' | 'warn' | 'low';

export const EXCEPTION_TYPE_LABELS: Record<ExceptionType, string> = {
  staleYmsEmptySpot: 'Stale YMS Trailer in Empty Spot',
  fuzzyMismatch: "Fuzzy read doesn't match YMS",
  ghostTrailer: 'Ghost trailer'
};

/** The feed a person's correction is recorded on (its source is `manual`). */
export const MANUAL_CORRECTIONS_FEED = 'manual_corrections';

const isManual = (row: YardObservationDecision) =>
  row.feed === MANUAL_CORRECTIONS_FEED || row.source === 'manual';

// Why flux held a correction instead of placing the trailer, in words. Anything
// not listed falls back to the humanised reason.
const HOLD_REASON_MESSAGES: Record<string, string> = {
  destination_occupied: 'another trailer is in that spot',
  fuzzy_no_match: "the zone name doesn't match one zone",
  no_staging_zone: 'the read only named the facility, and it has no staging zone',
  active_at_other_campus: 'another campus saw this trailer more recently',
  conflicting_open_task: 'someone already has a move task open for this trailer',
  locked: 'another move holds the lock on the spot or trailer',
  rules_blocked: 'the movement rules refused the move'
};

/** Why a correction was held, for a person — with the engine's message for `rules_blocked`. */
export function holdReasonText(reason: string, error?: string | null): string {
  const why = HOLD_REASON_MESSAGES[reason] ?? humanize(reason).toLowerCase();
  return reason === 'rules_blocked' && error ? `${why} (${error})` : why;
}

// Same 30-minute line the Overview tab uses between "fresh" and "stale".
const STALE_MINUTES = 30;
const LOW_CONFIDENCE_SCORE = 0.6;

export interface ExceptionField {
  label: string;
  value: string;
  /** Render the value as a chip rather than plain text. */
  tag?: boolean;
}

export interface YardException {
  /** The decision row this view was built from — what the detail modal is keyed by. */
  id: string;
  /**
   * The exception the row is the newest hold of, stable while it stays open —
   * unlike `id`, which changes with every read that holds it again. Null for
   * rows that did not come from the exceptions query.
   */
  exceptionId: string | null;
  spot: string;
  /**
   * Null for a row that raises no exception — the detail modal also opens on
   * history rows such as moves — or one of a type this page does not know yet.
   */
  type: ExceptionType | null;
  severity: ExceptionSeverity;
  /** What the device read at the spot ("Empty" if nothing). */
  seen: string;
  /**
   * The trailer the yard system has, by flux's own number
   * (`current_location.trailer_number`), or null when it has no record — for
   * display, `expected` ("No record" when null). Rows recorded before flux
   * wrote that number fall back to the number the feed matched.
   */
  ymsTrailer: string | null;
  /** What the yard system holds for the spot, or "No record". */
  expected: string;
  /** The trailer number the read's OCR came back with, or null if it read none. */
  ocrReading: string | null;
  /**
   * The trailer number and SCAC on the card's newest decision — the camera's
   * number, or once a person has corrected it, what they entered. Prefills the
   * create-trailer form.
   */
  trailerNumber: string | null;
  scac: string | null;
  /**
   * The SCAC of the trailer flux already has with this number, for a ghost
   * trailer that left or is active somewhere else; pre-fills the create form.
   */
  knownScac: string | null;
  /**
   * Set when the newest decision is a person's held correction: "Trailer
   * created, not placed: <why>". The trailer exists; retrying reuses it.
   */
  heldCorrection: string | null;
  confidence: string;
  age: string;
  device: string;
  imageUrl: string | null;
  /** The uncropped annotated frame for the fullscreen view; falls back to imageUrl. */
  fullImageUrl: string | null;
  /** The current-read quadrant: read at, spotter, OCR read, brand (OCR), brand (expected), RTLS result, YMS reconciliation result. */
  readDetails: ExceptionField[];
  /** Ziggy's report page for the read, for FreightRoll staff; null when Ziggy sent none. */
  debugUrl: string | null;
  /** flux's plain-English account of the decision; null when flux sent none. */
  explanation: string | null;
  /**
   * The spot the map should highlight, independent of whether a device fix
   * exists — many hold reasons (unknown trailer, not observed) have no RTLS
   * read at all, but the decision still names a yard spot.
   */
  spotUuid: string | null;
  /** The spot's own name, straight off the decision row — null for a zone-level read. */
  rtlsSpotName: string | null;
  /** The RTLS-side zone name, from evidence.fuzzy.zone — null when the read has none. */
  rtlsZoneName: string | null;
  /**
   * The spot flux currently believes the trailer is at, from the decision's
   * `current_location` — independent of `spotUuid`, the spot the read
   * actually came from. The two differ exactly when the disagreement is
   * about location (a "wrong spot" exception); null when flux has no belief
   * to compare against.
   */
  expectedSpotUuid: string | null;
  /**
   * The YMS-side spot name for the location `expectedSpotUuid` names — straight
   * off `current_location.yard_spot_name`. Falls back to `dock_name` when flux
   * believes the trailer is at a dock rather than a yard spot (current_location
   * then has no yard_spot_uuid/name at all, only dock_uuid/dock_name).
   */
  ymsSpotName: string | null;
  /**
   * The zone flux has the trailer in: `current_location.facility_zone_name`, or
   * for a trailer at a dock the dock's warehouse zone (`warehouse_zone_name`) —
   * a dock belongs to a warehouse zone, not a facility zone. Null when flux has
   * neither.
   */
  ymsZoneName: string | null;
  /** Flux has the trailer at a dock door rather than a yard spot, so `ymsSpotName` is a door. */
  ymsAtDock: boolean;
  /** Where to place the camera marker on the map. Null when the read has no lat/lon. */
  reading: {
    lat: number;
    lon: number;
    heading: number | null;
  } | null;
}

export type AuditAction =
  | 'Move applied'
  | 'Status updated'
  | 'Task opened'
  | 'No change'
  | 'Matched'
  | 'Ignored'
  | 'Error'
  | 'Reverted';

/** A spot or door by name, with the zone it is in; shown as the name, the zone on hover. */
export interface SpotLabel {
  name: string;
  zone: string | null;
}

/** What a read called for, or what was done about it, in the audit log's terms. */
export type AuditStep = 'Move' | 'Match' | 'Ignore';

/** What was done about a read: a step, or a move flux tried and could not make. */
export type ExecutedStep = AuditStep | 'Error';

export interface AuditLogEntry {
  id: string;
  /** Raw ISO timestamp `time` was formatted from — sort by this, display `time`. */
  insertedAt: string;
  time: string;
  spot: string;
  trailer: string;
  /** Where the YMS had the trailer. Null when it had it nowhere. */
  expectedSpot: SpotLabel | null;
  /** Where RTLS read the trailer. Null for a row with no read. */
  suggestedSpot: SpotLabel | null;
  /** What RTLS's read called for. Null for a row with no read — a person's correction. */
  suggested: AuditStep | null;
  /** What flux did about it: `action`, in the same terms as `suggested`. */
  executed: ExecutedStep;
  action: AuditAction;
  /** What came of the row for the trailer: the humanised movement, or why it did not move. */
  movement: string;
  /** flux's plain-English account of the decision; the outcome when flux sent none. */
  explanation: string;
  actor: string;
  automatic: boolean;
  /** A person's correction (feed `manual_corrections`) rather than a camera read. */
  manual: boolean;
  /** Humanised kind of correction, e.g. "New trailer"; null for non-manual rows. */
  correction: string | null;
  /** The exception a manual row answers, to link it back to its card. */
  answersExceptionId: string | null;
  canUndo: boolean;
}

type Json = Record<string, any>;

// The json columns should arrive parsed, but tolerate a JSON string too.
const asObject = (value: Json | string | null | undefined): Json => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return value;
};

const present = (value: unknown): string | null =>
  value === null || value === undefined || value === '' ? null : String(value);

export const humanize = (snake: string | null | undefined): string => {
  if (!snake) return '—';
  const words = snake.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A reason's label on the page, where the humanised code would mislead. Anything
// not listed falls back to the humanised reason.
const REASON_LABELS: Record<string, string> = {
  already_there: 'Trailer already in matched spot',
  already_there_unsure: 'Trailer likely in matched spot',
  fuzzy_no_match: "Location doesn't match YMS",
  fuzzy_trailer_not_in_campus: 'Trailer not in campus, no carrier found'
};

// The camera noops the audit log shows as "Matched" — the read agrees with the
// YMS — and the outcome each shows. Every other noop shows as "No change".
const MATCH_OUTCOMES: Record<string, string> = {
  already_there: 'Reads Match YMS',
  already_there_unsure: 'Reads Match YMS',
  vacant_location: 'Empty, Matches YMS',
  recently_applied: 'Matches Recent Move'
};

const isMatch = (row: YardObservationDecision) =>
  row.decision === 'noop' && !isManual(row) && row.reason in MATCH_OUTCOMES;

export const reasonLabel = (reason: string | null | undefined): string =>
  (reason && REASON_LABELS[reason]) || humanize(reason);

const formatPercent = (fraction: number | null | undefined): string =>
  typeof fraction === 'number' ? `${(fraction * 100).toFixed(1)}%` : '—';

// The app's standard Date/Time display — plain `toLocaleString()`, as used
// throughout (e.g. TaskDetail, CarrierTrailers, Timeline).
export const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

// The UTC ISO timestamps flux writes into an explanation (2026-10-01T16:04:00Z).
const ISO_UTC_TIMESTAMP = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?Z/g;

/** Text with each UTC timestamp in it shown in the reader's local time, as the page shows dates. */
export const localizeTimes = (text: string): string =>
  text.replace(ISO_UTC_TIMESTAMP, (iso) => formatDateTime(iso));

// flux's exception types, as it sends them, to the names the page uses.
const EXCEPTION_TYPES_FROM_API: Record<string, ExceptionType> = {
  stale_yms_empty_spot: 'staleYmsEmptySpot',
  fuzzy_mismatch: 'fuzzyMismatch',
  ghost_trailer: 'ghostTrailer'
};

/**
 * The exception type flux gave a row: the open exception's own type when the
 * row carries one, else the row's. Null when the row raises no exception, or
 * raises one of a type this page does not know yet.
 */
export function exceptionTypeOf(row: YardObservationDecision): ExceptionType | null {
  const type = row.exception?.type ?? row.exceptionType;
  return (type && EXCEPTION_TYPES_FROM_API[type]) || null;
}

// The door a read was taken at. flux files a door read under its dock, which
// the row carries by uuid only, so the name comes from the read itself.
const readDockName = (row: YardObservationDecision): string | null =>
  present(row.rtlsHistory?.location?.dock_name);

// The spot or door the read put the trailer at, by name.
const readSlotName = (row: YardObservationDecision): string | null =>
  present(row.yardSpotName) ?? readDockName(row);

// A place by name and zone, or the zone alone when there is no spot or door.
const spotLabel = (name: string | null, zone: string | null): SpotLabel | null => {
  if (name) return { name, zone };
  return zone ? { name: zone, zone: null } : null;
};

// A fuzzy read's range of spots, as "60–68", or one spot when it is a range of one.
function readRange(row: YardObservationDecision): string | null {
  const range = row.rtlsHistory?.location?.range;
  const lo = present(range?.lo);
  const hi = present(range?.hi);
  if (!lo || !hi) return null;
  return lo === hi ? lo : `${lo}–${hi}`;
}

/** Where the YMS had the trailer: its spot or door, in its zone. */
export function expectedSpotOf(row: YardObservationDecision): SpotLabel | null {
  const current = asObject(row.currentLocation);
  return spotLabel(
    present(current.yard_spot_name) ?? present(current.dock_name),
    present(current.facility_zone_name) ?? present(current.warehouse_zone_name)
  );
}

/**
 * Where RTLS read the trailer: its spot or door, else a fuzzy read's range, in
 * the zone the read names. A range names its zone only in the read's evidence.
 */
export function suggestedSpotOf(row: YardObservationDecision): SpotLabel | null {
  const location = row.rtlsHistory?.location;
  return spotLabel(
    readSlotName(row) ?? readRange(row),
    present(location?.facility_zone_name) ??
      present(location?.warehouse_zone_name) ??
      present(asObject(asObject(row.evidence).fuzzy).zone)
  );
}

// Where the read put the trailer, as a row or card title: its spot or door,
// else the zone a fuzzy read named, else how precise the read was. Null for a
// row that says nothing about place.
export function placeLabel(row: YardObservationDecision): string | null {
  const zone = present(asObject(asObject(row.evidence).fuzzy).zone);
  const slot = readSlotName(row);
  if (slot) return slot;
  if (zone) return `Zone ${zone}`;
  if (row.locationKind === 'zone') return 'Zone-level read';
  if (row.locationKind === 'facility') return 'Yard-level read';
  return null;
}

// The RTLS read behind a decision. The joined `rtlsHistory` row is authoritative
// when present; `evidence.read` is the snapshot taken at decision time and covers
// rows whose read has since gone (or never matched).
interface RtlsRead {
  verdict: string | null;
  ocrTrailer: string | null;
  ocrBrand: string | null;
  expectedBrand: string | null;
  timestamp: string | null;
  imageUrl: string | null;
  fullImageUrl: string | null;
  lat: number | null;
  lon: number | null;
  heading: number | null;
  /** The truck the camera rides on, by name. */
  spotterName: string | null;
  /** Ziggy's report page for the read. */
  debugUrl: string | null;
}

const firstNumber = (...values: unknown[]): number | null =>
  values.find((v): v is number => typeof v === 'number') ?? null;

function rtlsRead(row: YardObservationDecision): RtlsRead {
  const snapshot = asObject(asObject(row.evidence).read);
  const h = row.rtlsHistory;
  return {
    verdict: present(h?.verdict) ?? present(snapshot.verdict),
    ocrTrailer:
      present(h?.trailerNumberOcrReading) ?? present(snapshot.trailer_number_ocr_reading),
    ocrBrand: present(h?.trailerBrandOcrReading),
    expectedBrand: present(h?.trailerBrandExpected),
    timestamp: present(h?.timestamp) ?? present(snapshot.timestamp),
    imageUrl: rtlsPreviewImage(h),
    fullImageUrl: rtlsFullscreenImage(h),
    lat: firstNumber(h?.lat),
    lon: firstNumber(h?.lon),
    heading:
      h?.deviceHeading != null
        ? cameraHeading(h.deviceHeading, h.deviceOrientation)
        : null,
    spotterName: present(h?.spotter?.name),
    debugUrl: present(h?.debugUrl)
  };
}

// flux's explanation of the decision, its times in the reader's own time zone.
const explanationOf = (row: YardObservationDecision): string | null => {
  const explanation = present(row.explanation);
  return explanation && localizeTimes(explanation);
};

/**
 * The trailer number the YMS side shows: flux's own number for the trailer it
 * has (`current_location.trailer_number`), since flux is the YMS. Null when flux
 * has no trailer there — never the number the vendor expected or matched.
 */
export function ymsTrailerNumber(row: YardObservationDecision): string | null {
  return present(asObject(row.currentLocation).trailer_number);
}

function readFields(row: YardObservationDecision, read: RtlsRead): ExceptionField[] {
  const fields: Array<[string, string | null, boolean?]> = [
    ['Read at', read.timestamp ? formatDateTime(read.timestamp) : null],
    ['Spotter', read.spotterName?.toUpperCase() ?? '--'],
    ['OCR read', read.ocrTrailer],
    ['Brand (OCR)', read.ocrBrand],
    ['Brand (expected)', read.expectedBrand],
    ['RTLS Result', read.verdict, true],
    [
      'YMS Reconciliation Result',
      row.movementType ? `Move: ${humanize(row.movementType)}` : reasonLabel(row.reason),
      true
    ]
  ];
  return fields
    .filter((f): f is [string, string, boolean | undefined] => f[1] !== null)
    .map(([label, value, tag]) => ({ label, value, tag }));
}

export function toYardException(row: YardObservationDecision, now: Date): YardException {
  const type = exceptionTypeOf(row);
  const evidence = asObject(row.evidence);
  const read = rtlsRead(row);
  const fuzzy = asObject(evidence.fuzzy);
  const currentLocation = asObject(row.currentLocation);

  const observed = row.observedAt ?? row.insertedAt;
  const minutes = ageMinutes(observed, now);
  const age =
    minutes === null
      ? '—'
      : `${formatAge(observed, now)} ago`.replace('now ago', 'just now');

  const seenTrailer = read.ocrTrailer ?? present(row.trailerNumber);
  const seen = type === 'staleYmsEmptySpot' ? 'Empty' : (seenTrailer ?? '—');
  const ymsTrailer = ymsTrailerNumber(row);
  const expected = ymsTrailer ?? 'No record';

  let severity: ExceptionSeverity = 'open';
  if (row.score !== null && row.score < LOW_CONFIDENCE_SCORE) {
    severity = 'low';
  } else if (minutes !== null && minutes > STALE_MINUTES) {
    severity = 'warn';
  }

  return {
    id: row.id,
    exceptionId: row.exception?.id ?? null,
    spot:
      readSlotName(row) ??
      (row.locationKind === 'facility' ? 'Yard-level read' : 'Unknown location'),
    type,
    severity,
    seen,
    ymsTrailer,
    expected,
    ocrReading: read.ocrTrailer,
    trailerNumber: present(row.trailerNumber),
    scac: present(row.scac),
    knownScac: present(asObject(row.explanationContext).scac),
    heldCorrection:
      isManual(row) && row.decision === 'hold'
        ? `Trailer created, not placed: ${holdReasonText(row.reason, row.error)}`
        : null,
    confidence: formatPercent(row.score),
    age,
    // When the image was captured — no device or feed name, which aren't
    // anything a person reviewing an exception can act on.
    device: formatDateTime(read.timestamp ?? observed),
    imageUrl: read.imageUrl,
    fullImageUrl: read.fullImageUrl,
    readDetails: readFields(row, read),
    debugUrl: read.debugUrl,
    explanation: explanationOf(row),
    spotUuid: row.yardSpotUuid ?? row.dockUuid,
    rtlsSpotName: readSlotName(row),
    rtlsZoneName: present(fuzzy.zone),
    expectedSpotUuid: present(currentLocation.yard_spot_uuid),
    ymsSpotName:
      present(currentLocation.yard_spot_name) ?? present(currentLocation.dock_name),
    ymsZoneName:
      present(currentLocation.facility_zone_name) ??
      present(currentLocation.warehouse_zone_name),
    ymsAtDock:
      !present(currentLocation.yard_spot_uuid) &&
      Boolean(present(currentLocation.dock_uuid)),
    reading:
      read.lat !== null && read.lon !== null
        ? { lat: read.lat, lon: read.lon, heading: read.heading }
        : null
  };
}

// The reasons flux gives for writing a new state at the trailer's current
// place: its status (and maybe a shipment) changed, the trailer did not move.
const STATUS_WRITE_REASONS = new Set([
  'status_changed',
  'status_and_shipment',
  'status_without_shipment'
]);

const isStatusWrite = (row: YardObservationDecision) =>
  row.decision === 'applied' && !row.movementType && STATUS_WRITE_REASONS.has(row.reason);

function auditActionOf(row: YardObservationDecision): AuditAction {
  if (isStatusWrite(row)) return 'Status updated';
  if (isMatch(row)) return 'Matched';
  switch (row.decision) {
    case 'applied':
      return 'Move applied';
    case 'task_opened':
      return 'Task opened';
    // A hold is shown as ignored: nothing was changed in the yard.
    case 'hold':
    case 'ignore':
      return 'Ignored';
    case 'noop':
      return 'No change';
    default:
      return 'Error';
  }
}

// Verdicts that only say the spot and the YMS differ, without placing the
// trailer anywhere: RTLS funnels them as conflicts, but they suggest no move.
const REVIEW_VERDICTS = new Set(['disagree', 'spot_conflict']);

// RTLS's own call on the read (its funnel outcome): it agrees with the YMS, or it
// puts the trailer somewhere the YMS does not. Anything else RTLS sends is
// nothing to act on. A person's correction is no RTLS read, so it suggests nothing.
function suggestedStepOf(row: YardObservationDecision): AuditStep | null {
  if (isManual(row) || !row.rtlsHistory) return null;
  if (REVIEW_VERDICTS.has(row.rtlsHistory.verdict ?? '')) return 'Ignore';
  switch (row.rtlsHistory.funnel?.outcome) {
    case 'confirmed':
    case 'fuzzy_agree':
    case 'empty':
      return 'Match';
    case 'conflict':
      return 'Move';
    default:
      return 'Ignore';
  }
}

/** What flux did, in the same terms as what RTLS suggested. */
export function executedStepOf(action: AuditAction): ExecutedStep {
  switch (action) {
    case 'Move applied':
    case 'Task opened':
      return 'Move';
    // The trailer was already where the read put it.
    case 'Matched':
    case 'Status updated':
      return 'Match';
    case 'Error':
      return 'Error';
    // Nothing changed in the yard: held, ignored or undone.
    default:
      return 'Ignore';
  }
}

// What came of the row for the trailer: where it went, or why it did not move.
function auditOutcomeOf(row: YardObservationDecision): string {
  if (isStatusWrite(row)) return 'Not moved · status overwritten';
  if (isMatch(row)) return MATCH_OUTCOMES[row.reason];
  return row.movementType ? humanize(row.movementType) : reasonLabel(row.reason);
}

export function toAuditEntries(
  rows: YardObservationDecision[],
  now: Date
): AuditLogEntry[] {
  return rows.map((row) => {
    const action = auditActionOf(row);
    const manual = isManual(row);
    const automatic = !manual && (row.actor === null || row.actor === 'system');
    return {
      id: row.id,
      insertedAt: row.insertedAt,
      time: formatDateTime(row.insertedAt),
      spot: placeLabel(row) ?? '—',
      trailer: present(row.trailerNumber) ?? '—',
      expectedSpot: expectedSpotOf(row),
      suggestedSpot: suggestedSpotOf(row),
      suggested: suggestedStepOf(row),
      executed: executedStepOf(action),
      action,
      movement: auditOutcomeOf(row),
      explanation: explanationOf(row) ?? auditOutcomeOf(row),
      actor: manual
        ? (present(row.requestedBy) ?? 'Unknown')
        : automatic
          ? 'System'
          : row.actor!,
      automatic,
      manual,
      correction: manual && row.correction ? humanize(row.correction) : null,
      answersExceptionId: present(row.answersExceptionId),
      canUndo: false
    };
  });
}

/** Actions the system took by itself (applied or task opened) in the last `hours` hours. */
export function countAutoResolved(
  rows: YardObservationDecision[],
  now: Date,
  hours: number
): number {
  return rows.filter((row) => {
    if (row.decision !== 'applied' && row.decision !== 'task_opened') return false;
    const minutes = ageMinutes(row.insertedAt, now);
    return minutes !== null && minutes <= hours * 60;
  }).length;
}

export type CreateTrailerOutcome =
  /** Resolved — placed, or flux already had it there. Remove the card. */
  | { kind: 'resolved'; message: string }
  /** Created but held; the exception stays open and a retry is safe. */
  | { kind: 'held'; message: string }
  /** The engine failed; the exception stays open. */
  | { kind: 'error'; message: string }
  /** Nothing was recorded (facility not activated). */
  | { kind: 'unrecorded'; message: string };

/** What a `createUnknownTrailer` response means, and what to tell the person. */
export function createTrailerOutcome(
  result: CreateUnknownTrailerResult,
  trailerNumber: string
): CreateTrailerOutcome {
  const { decision, exception } = result;
  if (!decision) {
    return { kind: 'unrecorded', message: 'Trailer created, not placed.' };
  }
  if (decision.decision === 'error') {
    return {
      kind: 'error',
      message: decision.error || 'The engine failed to place the trailer.'
    };
  }
  const trailer = present(decision.trailerNumber) ?? trailerNumber;
  if (exception.status === 'resolved') {
    if (decision.decision === 'noop') {
      return { kind: 'resolved', message: `${trailer} was already there.` };
    }
    // external_to_yard lands it at the spot; create_lineage, in the read's zone.
    const where = decision.yardSpotName ? `at ${decision.yardSpotName}` : 'in its zone';
    return { kind: 'resolved', message: `Created ${trailer} ${where}.` };
  }
  return {
    kind: 'held',
    message: `Trailer created, not placed: ${holdReasonText(decision.reason, decision.error)}.`
  };
}

/**
 * A place as "<zone> - <dock or spot>", or whichever half is known — the zone
 * alone for a zone-level place. Null when neither is.
 */
export const zonedPlaceLabel = (
  zoneName: string | null | undefined,
  name: string | null | undefined
): string | null =>
  [present(zoneName), present(name)].filter(Boolean).join(' - ') || null;

/** Where flux has a matched trailer, as `zonedPlaceLabel` says it. */
export const matchLocationLabel = (match: GhostTrailerMatch) =>
  zonedPlaceLabel(match.zoneName, match.locationName);

/** What the person typed into the Add Trailer modal, to set beside a match. */
export interface EnteredTrailer {
  scac: string;
  trailerNumber: string;
  locationUuid: string | null;
  /** As `zonedPlaceLabel` says it. */
  locationName: string | null;
}

/** One line of the Possible Match dialog: the match's value beside the person's. */
export interface MatchComparisonRow {
  label: string;
  matched: string;
  entered: string;
  same: boolean;
}

// Numbers and SCACs as flux compares them: trimmed and upper-cased.
const normalizeCode = (value: string | null | undefined) =>
  (value ?? '').trim().toUpperCase();

const sameCode = (a: string | null | undefined, b: string | null | undefined) =>
  normalizeCode(a) !== '' && normalizeCode(a) === normalizeCode(b);

// The same place by uuid; flux's names and the picker's can differ in case.
const samePlace = (a: string | null | undefined, b: string | null | undefined) =>
  Boolean(a && b && a.toLowerCase() === b.toLowerCase());

/**
 * The matched trailer beside what the person entered — SCAC, trailer number
 * and location — and whether each agrees, for the Possible Match dialog.
 */
export function compareGhostTrailerMatch(
  match: GhostTrailerMatch,
  entered: EnteredTrailer
): MatchComparisonRow[] {
  return [
    {
      label: 'SCAC',
      matched: present(match.scac) ?? '—',
      entered: present(entered.scac) ?? '—',
      same: sameCode(match.scac, entered.scac)
    },
    {
      label: 'Trailer #',
      matched: present(match.trailerNumber) ?? '—',
      entered: present(entered.trailerNumber) ?? '—',
      same: sameCode(match.trailerNumber, entered.trailerNumber)
    },
    {
      label: 'Location',
      matched: matchLocationLabel(match) ?? '—',
      entered: present(entered.locationName) ?? '—',
      same: samePlace(match.locationUuid, entered.locationUuid)
    }
  ];
}
